import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import {
  inspectionReportService,
  type InspectionReport,
  type ReportReadiness,
} from '@/services/inspection-report.service';
import { availableReportActions, type ReportActionKey } from '../../report/report-workflow';

interface Props {
  inspectionId: string;
  report: InspectionReport;
  readiness: ReportReadiness | undefined;
  has: (permission: string) => boolean;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

const ACTION_LABELS: Record<ReportActionKey, string> = {
  generate: 'Generate',
  submit: 'Submit for Review',
  approve: 'Approve',
  reject: 'Reject',
  reopen: 'Reopen',
  publish: 'Publish',
  unpublish: 'Unpublish',
};

const SUCCESS: Record<ReportActionKey, string> = {
  generate: 'Report generated',
  submit: 'Submitted for review',
  approve: 'Report approved',
  reject: 'Report rejected',
  reopen: 'Report reopened',
  publish: 'Published to customer',
  unpublish: 'Unpublished',
};

export function ReportWorkflowActions({ inspectionId, report, readiness, has }: Props) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: (key: ReportActionKey): Promise<InspectionReport> => {
      switch (key) {
        case 'generate':
          return inspectionReportService.generate(inspectionId);
        case 'submit':
          return inspectionReportService.submit(inspectionId);
        case 'approve':
          return inspectionReportService.approve(inspectionId);
        case 'reject':
          return inspectionReportService.reject(inspectionId, reason.trim());
        case 'reopen':
          return inspectionReportService.reopen(inspectionId);
        case 'publish':
          return inspectionReportService.publish(inspectionId);
        case 'unpublish':
          return inspectionReportService.unpublish(inspectionId);
      }
    },
    onSuccess: (_data, key) => {
      toast.success(SUCCESS[key]);
      setRejecting(false);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['report', inspectionId] });
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
      void qc.invalidateQueries({ queryKey: ['report-history', inspectionId] });
    },
    onError: (e: Error, key) => toast.error(extractMsg(e, `Failed to ${key} report`)),
  });

  const actions = availableReportActions(report, has, readiness);
  const pendingKey = mutation.isPending ? (mutation.variables as ReportActionKey) : null;

  if (actions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No workflow actions available in this state.</p>
    );
  }

  return (
    <div className="space-y-3" data-testid="report-workflow-actions">
      <div className="flex flex-wrap gap-2">
        {actions.map((key) =>
          key === 'reject' ? (
            <Button
              key={key}
              type="button"
              variant="secondary"
              disabled={mutation.isPending}
              onClick={() => setRejecting((v) => !v)}
              data-testid="action-reject"
            >
              {ACTION_LABELS[key]}
            </Button>
          ) : (
            <Button
              key={key}
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(key)}
              data-testid={`action-${key}`}
            >
              {pendingKey === key ? 'Working…' : ACTION_LABELS[key]}
            </Button>
          ),
        )}
      </div>

      {rejecting && (
        <div
          className="rounded-lg border border-border bg-background p-3"
          data-testid="reject-panel"
        >
          <label className="mb-1 block text-sm font-medium text-foreground">
            Rejection reason (required)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Explain what needs to be corrected…"
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={reason.trim().length === 0 || mutation.isPending}
              onClick={() => mutation.mutate('reject')}
              data-testid="confirm-reject"
            >
              {pendingKey === 'reject' ? 'Rejecting…' : 'Confirm Reject'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
